import { jsx, Fragment } from 'react/jsx-runtime';
import { createContext, forwardRef, useLayoutEffect, useState, useCallback, useMemo, useId, Fragment as Fragment$1 } from 'react';
import clsx from 'clsx';
import { useSharedRef } from '../hooks/use-shared-ref.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { range } from '../utils/array.js';
import { getVarName, px, rems } from '../utils/css.js';
import { seedFromId, lineWidth, SHIMMER_VIEWPORT_DURATION_MS, SHIMMER_BAND_WIDTH_PX } from './skeleton.utils.js';
import { shimmerStartPosition, shimmerEndPosition, shimmerDuration, root, shimmer, reducedMotionPulse, base, bone, boneRadius, text, textSizing, textHeadingSize, textBodySize } from './skeleton.css.js';

const SkeletonAnimationContext = createContext(null);
const shimmerStartPositionVar = getVarName(shimmerStartPosition);
const shimmerEndPositionVar = getVarName(shimmerEndPosition);
const shimmerDurationVar = getVarName(shimmerDuration);
/**
 * Accessibility wrapper for a group of skeleton blocks.
 * Sets `aria-busy="true"` and defaults to `role="status"` so screen readers announce the region
 * as loading.
 */
const Root = forwardRef(({ role = 'status', className, ...props }, ref) => {
    const animationManager = useSkeletonAnimationManager();
    const elementRef = useSharedRef(ref);
    useLayoutEffect(() => {
        const element = elementRef.current;
        if (element == null)
            return undefined;
        const { left, right, width } = element.getBoundingClientRect();
        const duration = (SHIMMER_VIEWPORT_DURATION_MS * (width + SHIMMER_BAND_WIDTH_PX)) /
            (window.innerWidth + SHIMMER_BAND_WIDTH_PX);
        element.style.setProperty(shimmerStartPositionVar, `${left}px`);
        element.style.setProperty(shimmerEndPositionVar, `${right}px`);
        element.style.setProperty(shimmerDurationVar, `${Math.round(duration)}ms`);
    }, [elementRef]);
    return (jsx(SkeletonAnimationContext.Provider, { value: animationManager, children: jsx("div", { "data-fpl-component": "", ...props, ref: elementRef, className: clsx(root, className), role: role, "aria-busy": "true" }) }));
});
Root.displayName = 'Skeleton.Root';
/**
 * Renders its children `count` times.
 * Use for repeating skeleton rows or list items without manual boilerplate.
 * Progressive fading uses a render function so consumers can apply the generated style to the
 * element that represents each item without introducing layout wrappers.
 */
function Repeat(props) {
    const { count, children, fadeAfter } = props;
    function getProgressiveFadeOpacity(index, totalCount, solidItems) {
        const clampedSolidCount = Math.max(0, Math.min(solidItems, totalCount));
        if (index < clampedSolidCount)
            return 1;
        const fadeCount = totalCount - clampedSolidCount;
        return (totalCount - index) / (fadeCount + 1);
    }
    return (jsx(Fragment, { children: range(count, (index) => {
            const content = typeof children === 'function'
                ? children({
                    index,
                    style: {
                        opacity: getProgressiveFadeOpacity(index, count, fadeAfter ?? 2),
                    },
                })
                : children;
            return jsx(Fragment$1, { children: content }, index);
        }) }));
}
Repeat.displayName = 'Skeleton.Repeat';
function useSkeletonAnimationManager() {
    // This mutable registry is imperative bookkeeping and does not drive rendered output.
    const [animations] = useState(() => new Set());
    /** Returns a registered animation to use as the shared timeline reference. */
    const getReferenceAnimation = useCallback(() => animations.values().next().value, [animations]);
    /** Aligns an animation with the reference while both remain registered. */
    const syncStartTime = useCallback((animation, referenceAnimation) => {
        if (animations.has(referenceAnimation) &&
            animations.has(animation) &&
            referenceAnimation.startTime != null) {
            animation.startTime = referenceAnimation.startTime;
        }
    }, [animations]);
    /** Registers an animation for synchronization and returns its cleanup callback. */
    const register = useCallback((animation) => {
        const referenceAnimation = getReferenceAnimation();
        animations.add(animation);
        if (referenceAnimation?.startTime != null) {
            syncStartTime(animation, referenceAnimation);
        }
        else if (referenceAnimation != null) {
            // An animation's ready promise rejects when it is canceled during unmount.
            void referenceAnimation.ready
                .then(() => syncStartTime(animation, referenceAnimation))
                .catch(() => undefined);
        }
        return () => animations.delete(animation);
    }, [animations, getReferenceAnimation, syncStartTime]);
    return useMemo(() => ({ register }), [register]);
}
/**
 * A general-purpose skeleton block for custom dimensions.
 * Use when `Skeleton.Text`, `Skeleton.Avatar`, and `Skeleton.Icon` don't fit —
 * e.g. thumbnails, cards, pills, or buttons.
 */
const Bone = forwardRef(({ width, height, borderRadius, className, style, ...props }, ref) => {
    const animationManager = useValidatedContext(SkeletonAnimationContext, 'Skeleton.Bone', 'Skeleton.Root');
    const elementRef = useSharedRef(ref);
    useLayoutEffect(() => {
        const animations = elementRef.current?.getAnimations?.() ?? [];
        const skeletonAnimation = animations.find((candidate) => 'animationName' in candidate &&
            (candidate.animationName === shimmer ||
                candidate.animationName === reducedMotionPulse));
        if (skeletonAnimation == null)
            return undefined;
        return animationManager.register(skeletonAnimation);
    }, [animationManager, elementRef]);
    return (jsx("div", { "data-fpl-component": "", ...props, ref: elementRef, "aria-hidden": "true", className: clsx(base, bone, borderRadius && boneRadius[borderRadius], className), style: {
            ...style,
            ...(width != null && {
                width: width === 'fill' ? '100%' : px(width),
            }),
            ...(height != null && {
                height: height === 'fill' ? '100%' : px(height),
            }),
        } }));
});
Bone.displayName = 'Skeleton.Bone';
/**
 * A skeleton block sized to represent a line of text.
 * Use `lines` to render multiple lines at once.
 */
const Text = forwardRef(({ size, variant = 'body', width, lines = 1, ...props }, ref) => {
    const id = useId();
    const seed = seedFromId(id);
    const sizeClass = variant === 'heading' ? textHeadingSize[size] : textBodySize[size];
    return (jsx("div", { "data-fpl-component": "", ...props, ref: ref, "aria-hidden": "true", style: width == null ? { width: '100%' } : undefined, children: range(lines, (i) => (jsx(Bone, { className: clsx(text, textSizing, sizeClass), style: {
                width: width != null ? px(width) : lineWidth(i, seed, lines),
            } }, i))) }));
});
Text.displayName = 'Skeleton.Text';
const iconSizeMap = { sm: rems(16), md: rems(24) };
/**
 * A skeleton block matching FPL icon sizes.
 * Use this instead of `Skeleton.Bone` when the placeholder represents an icon.
 */
const Icon = forwardRef(({ size, ...props }, ref) => {
    const s = iconSizeMap[size];
    return jsx(Bone, { "data-fpl-component": "", ref: ref, style: { width: s, height: s }, ...props });
});
Icon.displayName = 'Skeleton.Icon';
const avatarSizeMap = { sm: rems(16), md: rems(24), lg: rems(32), xlg: rems(48) };
const avatarSquareRadius = { sm: 'sm', md: 'md', lg: 'md', xlg: 'lg' };
/**
 * A skeleton block matching FPL `Avatar` sizes and shapes.
 * Use this instead of `Skeleton.Bone` when the placeholder represents an avatar.
 */
const Avatar = forwardRef(({ size, shape = 'circle', ...props }, ref) => {
    const s = avatarSizeMap[size];
    const radius = shape === 'circle' ? 'full' : avatarSquareRadius[size];
    return (jsx(Bone, { "data-fpl-component": "", ref: ref, style: { width: s, height: s }, borderRadius: radius, ...props }));
});
Avatar.displayName = 'Skeleton.Avatar';

export { Avatar, Bone, Icon, Repeat, Root, Text };
//# sourceMappingURL=skeleton.js.map
